import React, { useCallback, useMemo } from "react"

import {
  getSupportedFilterTypesForSide,
  isAgentOnlyFilterManager,
  isSingleSpeakerFilterManager,
} from "@/metrics/runtime"
import { Button } from "@coval/ui/button"
import { Tooltip } from "@coval/ui/tooltip"
import { Info, Plus } from "lucide-react"
import { useTheme } from "next-themes"

import type {
  AudioFilterConfig,
  AudioFilterEntryConfig,
  AudioFilterJoin,
} from "@/types/metrics/metadata"

import {
  DraggableFilterList,
  JoinToggle,
  useFilterListSection,
} from "../../filter-list"
import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../../styles"
import { useAudioFilterReadOnly } from "../contexts/AudioFilterReadOnlyContext"
import {
  createDefaultAudioFilterEntry,
  createDefaultSpeakerFilter,
  MAX_AUDIO_FILTERS_PER_CATEGORY,
  SILENCE_MIN_GAP_DEFAULT_SECONDS,
  type AudioFilterEntryType,
  type AudioFilterPreset,
} from "../types"
import { AudioPreview } from "./AudioPreview"
import { FilterRow } from "./FilterRow"

interface AudioFilterSectionProps {
  value: AudioFilterConfig | undefined
  onChange: (value: AudioFilterConfig | undefined) => void
  metricManager?: string
  /** Lock the include side to one speaker, as the metric's directory entry requires. */
  lockToSingleSpeaker?: boolean
}

function useAudioFilter(
  value: AudioFilterConfig | undefined,
  onChange: (value: AudioFilterConfig | undefined) => void,
) {
  const activePreset: AudioFilterPreset = useMemo(
    () => (value === undefined ? "full" : "custom"),
    [value],
  )

  const handlePresetChange = useCallback(
    (preset: AudioFilterPreset) => {
      if (preset === "full") {
        onChange(undefined)
      } else if (preset === "custom" && activePreset !== "custom") {
        onChange({ include: [], exclude: [] })
      }
    },
    [activePreset, onChange],
  )

  const handleConfigChange = useCallback(
    (next: AudioFilterConfig) => {
      onChange(next)
    },
    [onChange],
  )

  return { activePreset, handlePresetChange, handleConfigChange }
}

function formatAudioFilter(filter: AudioFilterEntryConfig): string {
  switch (filter.type) {
    case "speaker":
      return filter.speakers
        .map((speaker) => speaker.charAt(0).toUpperCase() + speaker.slice(1))
        .join(" or ")
    case "silence":
      return `Silence of at least ${filter.min_gap_seconds ?? SILENCE_MIN_GAP_DEFAULT_SECONDS}s`
    case "overlap":
      return "Overlapping speech"
    case "music":
      return "Music"
    case "time_range": {
      const position = {
        first_n_seconds: "First",
        last_n_seconds: "Last",
        middle_n_seconds: "Middle",
      }[filter.mode]
      return `${position} ${filter.count}s`
    }
    case "turn_range": {
      const position = filter.mode === "first_n" ? "First" : "Last"
      const speaker =
        filter.speaker && filter.speaker !== "any" ? ` ${filter.speaker}` : ""
      return `${position} ${filter.count}${speaker} turns`
    }
  }
}

function formatAudioFilterList(
  filters: AudioFilterEntryConfig[],
  joins: AudioFilterJoin[] | undefined,
): string {
  return filters
    .map((filter, index) => {
      const description = formatAudioFilter(filter)
      if (index === 0) {
        return description
      }
      return `${(joins?.[index - 1] ?? "or").toUpperCase()} ${description}`
    })
    .join(" ")
}

export const AudioFilterToggle: React.FC<AudioFilterSectionProps> = ({
  value,
  onChange,
}) => {
  const readOnly = useAudioFilterReadOnly()
  const { theme } = useTheme()
  const { activePreset, handlePresetChange } = useAudioFilter(value, onChange)
  const scopeLabelId = React.useId()

  const selectedStyles = `${theme === "dark" ? "bg-surface-2" : "bg-white"} text-coval-text shadow-sm`
  const unselectedStyles = "text-coval-text-muted hover:text-coval-text"

  const presets: { key: AudioFilterPreset; label: string }[] = [
    { key: "full", label: "Full" },
    { key: "custom", label: "Custom" },
  ]

  if (readOnly) {
    if (activePreset === "full") {
      return null
    }
    return (
      <div className={FIELD_CONTAINER_STYLES}>
        <p className={LABEL_STYLES}>Audio Scope</p>
        <p className="text-sm text-coval-text">Custom</p>
      </div>
    )
  }

  return (
    <div className={`${FIELD_CONTAINER_STYLES} flex-1`}>
      <div className={LABEL_ROW_STYLES}>
        <span id={scopeLabelId} className={LABEL_STYLES}>
          Audio Scope
        </span>
        <Tooltip
          content={
            <div className="max-w-xs p-1">
              <p className="text-sm">
                Choose which regions of the call audio to evaluate. Use filters
                to focus on speakers, silence, music, or specific time/turn
                ranges.
              </p>
            </div>
          }
        >
          <Info className={INFO_ICON_STYLES} />
        </Tooltip>
      </div>

      <div
        role="group"
        aria-labelledby={scopeLabelId}
        className="flex h-control rounded-md bg-surface-1 p-1"
      >
        {presets.map((preset) => (
          <button
            key={preset.key}
            type="button"
            aria-label={`Audio scope ${preset.label}`}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handlePresetChange(preset.key)
            }}
            className={`flex-1 px-3 text-sm font-medium rounded-sm transition-all cursor-pointer ${
              activePreset === preset.key ? selectedStyles : unselectedStyles
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export const AudioFilterDetails: React.FC<AudioFilterSectionProps> = ({
  value,
  onChange,
  metricManager,
  lockToSingleSpeaker = false,
}) => {
  const readOnly = useAudioFilterReadOnly()
  const { activePreset, handleConfigChange } = useAudioFilter(value, onChange)

  if (activePreset !== "custom" || !value) {
    return null
  }

  if (readOnly) {
    const includeFilters = value.include ?? []
    const excludeFilters = value.exclude ?? []

    if (includeFilters.length === 0 && excludeFilters.length === 0) {
      return (
        <p className="text-sm text-coval-text-muted">No filters configured.</p>
      )
    }

    return (
      <div className="space-y-2 text-sm">
        {includeFilters.length > 0 ? (
          <div>
            <p className={LABEL_STYLES}>Include</p>
            <p className="text-coval-text">
              {formatAudioFilterList(includeFilters, value.include_joins)}
            </p>
          </div>
        ) : null}
        {excludeFilters.length > 0 ? (
          <div>
            <p className={LABEL_STYLES}>Exclude</p>
            <p className="text-coval-text">
              {formatAudioFilterList(excludeFilters, value.exclude_joins)}
            </p>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="w-full">
      <AudioFilterLists
        config={value}
        onChange={handleConfigChange}
        metricManager={metricManager}
        lockToSingleSpeaker={lockToSingleSpeaker}
      />
    </div>
  )
}

export const AudioFilterSection: React.FC<AudioFilterSectionProps> = ({
  value,
  onChange,
  metricManager,
  lockToSingleSpeaker = false,
}) => {
  const readOnly = useAudioFilterReadOnly()
  if (readOnly && value === undefined) {
    return null
  }

  return (
    <div className={FIELD_CONTAINER_STYLES}>
      <AudioFilterToggle value={value} onChange={onChange} />
      <AudioFilterDetails
        value={value}
        onChange={onChange}
        metricManager={metricManager}
        lockToSingleSpeaker={lockToSingleSpeaker}
      />
    </div>
  )
}

interface AudioFilterListsProps {
  config: AudioFilterConfig
  onChange: (config: AudioFilterConfig) => void
  metricManager?: string
  lockToSingleSpeaker: boolean
}

const AudioFilterLists: React.FC<AudioFilterListsProps> = ({
  config,
  onChange,
  metricManager,
  lockToSingleSpeaker,
}) => {
  // Single-speaker mode (e.g. loop detection): one speaker include max, no
  // speaker exclude, single-select role. Other filter types still stack.
  const singleSpeakerMode =
    lockToSingleSpeaker || isSingleSpeakerFilterManager(metricManager ?? "")
  const agentOnlyMode = isAgentOnlyFilterManager(metricManager ?? "")
  const baseId = React.useId()
  const includeLabelId = `${baseId}-include`
  const excludeLabelId = `${baseId}-exclude`
  const includeBaseTypes = getSupportedFilterTypesForSide(
    metricManager ?? "",
    "include",
  )
  const excludeBaseTypes = getSupportedFilterTypesForSide(
    metricManager ?? "",
    "exclude",
  ).filter((type) => !lockToSingleSpeaker || type !== "speaker")
  const includeFilters = config.include ?? []
  const excludeFilters = config.exclude ?? []
  // Single-speaker metrics force "and" joins so time/turn ranges narrow within
  // the speaker's audio rather than unioning in other roles' audio.
  const forcedJoin: AudioFilterJoin | null = singleSpeakerMode ? "and" : null
  const includeJoins = forcedJoin
    ? (config.include_joins ?? []).map(() => forcedJoin)
    : (config.include_joins ?? [])
  const excludeJoins = forcedJoin
    ? (config.exclude_joins ?? []).map(() => forcedJoin)
    : (config.exclude_joins ?? [])

  // Hide "speaker" from a row's type dropdown if another row already holds one.
  // Always keep the row's own current type so the Select renders a matching
  // option (legacy configs may carry types now outside the allowed set).
  const includeAvailableTypesForRow = (
    rowFilter: AudioFilterEntryConfig,
  ): readonly AudioFilterEntryType[] => {
    const allowed = singleSpeakerMode
      ? (() => {
          const otherSpeakerExists = includeFilters.some(
            (entry) => entry !== rowFilter && entry.type === "speaker",
          )
          return otherSpeakerExists
            ? includeBaseTypes.filter((type) => type !== "speaker")
            : includeBaseTypes
        })()
      : includeBaseTypes
    return allowed.includes(rowFilter.type)
      ? allowed
      : [...allowed, rowFilter.type]
  }
  const excludeAvailableTypesForRow = (
    rowFilter: AudioFilterEntryConfig,
  ): readonly AudioFilterEntryType[] => {
    return excludeBaseTypes.includes(rowFilter.type)
      ? excludeBaseTypes
      : [...excludeBaseTypes, rowFilter.type]
  }

  // Default for "Add another": speaker if still usable, else first usable
  // type. Must filter before picking [0] -- a duplicate speaker would render
  // blank because the per-row dropdown hides it.
  const usableTypesForAdd = (
    allowed: readonly AudioFilterEntryType[],
    existing: AudioFilterEntryConfig[],
  ): readonly AudioFilterEntryType[] => {
    return allowed.filter((type) => {
      if (type === "speaker" && singleSpeakerMode) {
        return !existing.some((entry) => entry.type === "speaker")
      }
      return true
    })
  }
  const buildDefaultEntry = (
    allowed: readonly AudioFilterEntryType[],
    existing: AudioFilterEntryConfig[],
  ): AudioFilterEntryConfig => {
    const usable = usableTypesForAdd(allowed, existing)
    if (usable.includes("speaker")) {
      return createDefaultSpeakerFilter()
    }
    return createDefaultAudioFilterEntry(usable[0] ?? "time_range")
  }
  const includeHasAddableType =
    usableTypesForAdd(includeBaseTypes, includeFilters).length > 0
  const excludeHasAddableType =
    usableTypesForAdd(excludeBaseTypes, excludeFilters).length > 0

  const includeSection = useFilterListSection<AudioFilterEntryConfig>({
    filters: includeFilters,
    joins: includeJoins,
    max: MAX_AUDIO_FILTERS_PER_CATEGORY,
    createDefault: () => buildDefaultEntry(includeBaseTypes, includeFilters),
    defaultJoin: forcedJoin ?? undefined,
    onChange: (nextFilters, nextJoins) =>
      onChange({
        ...config,
        include: nextFilters,
        include_joins: nextJoins as AudioFilterJoin[] | undefined,
      }),
  })

  const excludeSection = useFilterListSection<AudioFilterEntryConfig>({
    filters: excludeFilters,
    joins: excludeJoins,
    max: MAX_AUDIO_FILTERS_PER_CATEGORY,
    createDefault: () => buildDefaultEntry(excludeBaseTypes, excludeFilters),
    defaultJoin: forcedJoin ?? undefined,
    onChange: (nextFilters, nextJoins) =>
      onChange({
        ...config,
        exclude: nextFilters,
        exclude_joins: nextJoins as AudioFilterJoin[] | undefined,
      }),
  })

  return (
    <div className="space-y-4">
      <div role="group" aria-labelledby={includeLabelId} className="space-y-2">
        <div className="flex items-center justify-between">
          <span id={includeLabelId} className={LABEL_STYLES}>
            Include
          </span>
        </div>
        {includeFilters.length === 0 ? (
          includeHasAddableType ? (
            <Button
              variant="ghost"
              onClick={includeSection.handleAdd}
              aria-label="Add include audio filter"
              className="text-coval-text-muted"
            >
              <Plus />
              Add include filter
            </Button>
          ) : null
        ) : (
          <div className="space-y-1">
            <DraggableFilterList<AudioFilterEntryConfig>
              filters={includeFilters}
              onChange={includeSection.handleUpdate}
              onRemove={includeSection.handleRemove}
              onReorder={includeSection.handleReorder}
              renderRow={({ filter, onChange: rowOnChange, onRemove }) => (
                <FilterRow
                  filter={filter}
                  onChange={rowOnChange}
                  onRemove={onRemove}
                  supportedFilterTypes={includeAvailableTypesForRow(filter)}
                  singleSpeakerSelect={singleSpeakerMode}
                  agentOnly={agentOnlyMode}
                />
              )}
              renderConnector={(index) =>
                forcedJoin ? (
                  <span className="text-xs text-coval-text-muted px-2">
                    and
                  </span>
                ) : (
                  <JoinToggle
                    value={includeJoins[index] ?? "or"}
                    onChange={(next) =>
                      includeSection.handleJoinChange(index, next)
                    }
                  />
                )
              }
              getKey={(filter, index) => filter.id ?? `include-${index}`}
              ariaPrefix="include"
            />
            {includeSection.belowCap && includeHasAddableType && (
              <Button
                variant="ghost"
                onClick={includeSection.handleAdd}
                aria-label="Add another include audio filter"
                className="text-coval-text-muted h-7 text-xs mt-2"
              >
                <Plus />
                Add another
              </Button>
            )}
          </div>
        )}
      </div>

      <div role="group" aria-labelledby={excludeLabelId} className="space-y-2">
        <div className="flex items-center justify-between">
          <span id={excludeLabelId} className={LABEL_STYLES}>
            Exclude
          </span>
        </div>
        {excludeFilters.length === 0 ? (
          excludeHasAddableType ? (
            <Button
              variant="ghost"
              onClick={excludeSection.handleAdd}
              aria-label="Add exclude audio filter"
              className="text-coval-text-muted"
            >
              <Plus />
              Add exclude filter
            </Button>
          ) : null
        ) : (
          <div className="space-y-1">
            <DraggableFilterList<AudioFilterEntryConfig>
              filters={excludeFilters}
              onChange={excludeSection.handleUpdate}
              onRemove={excludeSection.handleRemove}
              onReorder={excludeSection.handleReorder}
              renderRow={({ filter, onChange: rowOnChange, onRemove }) => (
                <FilterRow
                  filter={filter}
                  onChange={rowOnChange}
                  onRemove={onRemove}
                  supportedFilterTypes={excludeAvailableTypesForRow(filter)}
                  singleSpeakerSelect={singleSpeakerMode}
                  agentOnly={agentOnlyMode}
                />
              )}
              renderConnector={(index) =>
                forcedJoin ? (
                  <span className="text-xs text-coval-text-muted px-2">
                    and
                  </span>
                ) : (
                  <JoinToggle
                    value={excludeJoins[index] ?? "or"}
                    onChange={(next) =>
                      excludeSection.handleJoinChange(index, next)
                    }
                  />
                )
              }
              getKey={(filter, index) => filter.id ?? `exclude-${index}`}
              ariaPrefix="exclude"
            />
            {excludeSection.belowCap && excludeHasAddableType && (
              <Button
                variant="ghost"
                onClick={excludeSection.handleAdd}
                aria-label="Add another exclude audio filter"
                className="text-coval-text-muted h-7 text-xs mt-2"
              >
                <Plus />
                Add another
              </Button>
            )}
          </div>
        )}
      </div>

      <AudioPreview config={config} />
    </div>
  )
}
